/**
 * Centralized Logging Utility
 *
 * Provides environment-aware logging that automatically suppresses debug/info logs in production.
 * This prevents sensitive information (user emails, tokens, internal logic) from being exposed
 * in the browser console in production environments.
 *
 * @module utils/logger
 * @see documentation/security/SECURITY-HARDENING.md - "Reduce Verbose Console Logging in Production"
 *
 * @example
 * import { logger } from './utils/logger.js';
 *
 * // Development only - will be suppressed in production
 * logger.debug('Detailed debugging info', { userId: 123 });
 * logger.info('User signed in:', userEmail);
 *
 * // Always shown (warnings and errors)
 * logger.warn('API quota approaching limit');
 * logger.error('Authentication failed:', error);
 */

/**
 * Log levels in order of severity
 */
const LOG_LEVELS = {
    DEBUG: 0,   // Verbose debugging information (dev only)
    INFO: 1,    // General informational messages (dev only)
    WARN: 2,    // Warning messages (shown in all environments)
    ERROR: 3    // Error messages (shown in all environments)
};

/**
 * Determine current log level based on environment
 * Production: Only warnings and errors
 * Staging: Info and above (no verbose debug)
 * Development: All log levels
 */
const getDefaultLogLevel = () => {
    // Check for manual override in localStorage (development feature)
    if (typeof window !== 'undefined' && window.localStorage) {
        const override = localStorage.getItem('LOG_LEVEL');
        if (override && LOG_LEVELS[override.toUpperCase()] !== undefined) {
            return LOG_LEVELS[override.toUpperCase()];
        }
    }

    // Vite provides import.meta.env.MODE: 'development', 'production', or custom modes
    const mode = import.meta.env.MODE;

    // Environment-based defaults
    if (mode === 'production') {
        return LOG_LEVELS.WARN; // Production: only warnings and errors
    } else if (mode === 'staging') {
        return LOG_LEVELS.INFO; // Staging: info and above (no verbose debug)
    }

    return LOG_LEVELS.DEBUG; // Development: all logs including verbose debug
};

/**
 * Current active log level (can be changed at runtime in development)
 */
let CURRENT_LOG_LEVEL = getDefaultLogLevel();

/**
 * Check if the environment is production
 */
const isProduction = () => import.meta.env.MODE === 'production';

/**
 * Format log arguments with timestamp and level indicator
 * @private
 */
const formatLog = (level, args) => {
    const levelPrefix = `[${level}]`;
    return [levelPrefix, ...args];
};

/**
 * Centralized logger object with level-based logging methods
 */
export const logger = {
    /**
     * Log debug information (development only)
     * Use for: Detailed debugging, state changes, function calls
     * Production: Suppressed
     *
     * @param {...any} args - Arguments to log
     */
    debug: (...args) => {
        if (CURRENT_LOG_LEVEL <= LOG_LEVELS.DEBUG) {
            console.log(...formatLog('DEBUG', args));
        }
    },

    /**
     * Log informational messages (development only)
     * Use for: Application flow, user actions, successful operations
     * Production: Suppressed
     *
     * @param {...any} args - Arguments to log
     */
    info: (...args) => {
        if (CURRENT_LOG_LEVEL <= LOG_LEVELS.INFO) {
            console.log(...formatLog('INFO', args));
        }
    },

    /**
     * Log warning messages (shown in all environments)
     * Use for: Deprecated features, potential issues, recoverable errors
     * Production: Visible
     *
     * @param {...any} args - Arguments to log
     */
    warn: (...args) => {
        if (CURRENT_LOG_LEVEL <= LOG_LEVELS.WARN) {
            console.warn(...formatLog('WARN', args));
        }
    },

    /**
     * Log error messages (shown in all environments)
     * Use for: Exceptions, critical failures, unrecoverable errors
     * Production: Visible
     *
     * @param {...any} args - Arguments to log
     */
    error: (...args) => {
        if (CURRENT_LOG_LEVEL <= LOG_LEVELS.ERROR) {
            console.error(...formatLog('ERROR', args));
        }
    },

    /**
     * Check if debug logging is enabled
     * Useful for expensive debug operations that should only run in development
     *
     * @returns {boolean} True if debug logging is enabled
     * @example
     * if (logger.isDebugEnabled()) {
     *   const expensiveDebugData = computeExpensiveDebugInfo();
     *   logger.debug('Debug info:', expensiveDebugData);
     * }
     */
    isDebugEnabled: () => CURRENT_LOG_LEVEL <= LOG_LEVELS.DEBUG,

    /**
     * Check if info logging is enabled
     *
     * @returns {boolean} True if info logging is enabled
     */
    isInfoEnabled: () => CURRENT_LOG_LEVEL <= LOG_LEVELS.INFO,

    /**
     * Get current environment mode
     *
     * @returns {string} Current mode ('production', 'development', etc.)
     */
    getMode: () => import.meta.env.MODE,

    /**
     * Check if running in production
     *
     * @returns {boolean} True if in production mode
     */
    isProduction,

    /**
     * Get current log level name
     *
     * @returns {string} Current log level ('DEBUG', 'INFO', 'WARN', 'ERROR')
     */
    getLogLevel: () => Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === CURRENT_LOG_LEVEL) || 'UNKNOWN',

    /**
     * Set log level at runtime (development feature)
     * Persists to localStorage for future sessions
     *
     * @param {string} level - Log level ('DEBUG', 'INFO', 'WARN', 'ERROR')
     * @example
     * // In browser console:
     * logger.setLevel('DEBUG');  // Enable all logs
     * logger.setLevel('ERROR');  // Only show errors
     */
    setLevel: (level) => {
        const upperLevel = level.toUpperCase();
        if (LOG_LEVELS[upperLevel] !== undefined) {
            CURRENT_LOG_LEVEL = LOG_LEVELS[upperLevel];
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem('LOG_LEVEL', upperLevel);
            }
            console.log(`[LOGGER] Log level set to: ${upperLevel}`);
            console.log(`[LOGGER] Active levels: ${logger.getLogLevel()} and above`);
        } else {
            console.error(`[LOGGER] Invalid log level: ${level}. Use: DEBUG, INFO, WARN, ERROR`);
        }
    },

    /**
     * Reset log level to environment default
     * Clears localStorage override
     *
     * @example
     * logger.resetLevel();  // Back to environment default
     */
    resetLevel: () => {
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.removeItem('LOG_LEVEL');
        }
        CURRENT_LOG_LEVEL = getDefaultLogLevel();
        console.log(`[LOGGER] Log level reset to environment default: ${logger.getLogLevel()}`);
    },

    /**
     * Show current logger configuration
     * Useful for debugging logger setup
     */
    showConfig: () => {
        const config = {
            mode: import.meta.env.MODE,
            currentLevel: logger.getLogLevel(),
            debugEnabled: logger.isDebugEnabled(),
            infoEnabled: logger.isInfoEnabled(),
            override: typeof window !== 'undefined' && window.localStorage
                ? localStorage.getItem('LOG_LEVEL') || 'none'
                : 'N/A'
        };
        console.log('[LOGGER] Configuration:', config);
        console.log('\nAvailable commands:');
        console.log('  logger.setLevel("DEBUG")  - Enable all logs');
        console.log('  logger.setLevel("INFO")   - Info and above');
        console.log('  logger.setLevel("WARN")   - Warnings and errors only');
        console.log('  logger.setLevel("ERROR")  - Errors only');
        console.log('  logger.resetLevel()       - Reset to environment default');
        console.log('  logger.showConfig()       - Show this info');
        return config;
    }
};

/**
 * Log the logger initialization (only in development)
 */
if (!isProduction()) {
    console.log(`[LOGGER] Initialized in ${import.meta.env.MODE} mode | Log level: ${logger.getLogLevel()}`);
    console.log(`[LOGGER] Type "logger.showConfig()" for available commands`);
}

// Make logger available globally in development for easy console access
if (typeof window !== 'undefined' && !isProduction()) {
    window.logger = logger;
    console.log('[LOGGER] Available globally as window.logger');
}

/**
 * Default export for convenience
 */
export default logger;
